import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  technicianAPI,
  Technician,
  CreateTechnicianData,
  UpdateTechnicianData
} from '@/lib/supabase/technicians';
import { useCurrentTenantId } from '@/contexts/TenantContext';
import { logUserActivity } from '@/lib/activity/userActivity';
import { generateHumanDescription } from '@/lib/activity/humanDescriptions';

/**
 * Query function for technicians data (exported for prefetching)
 */
export async function fetchTechniciansData() {
  // Get technicians with zone information
  const technicians = await technicianAPI.getTechniciansWithCampaigns();

  // Cache zones for fast updates
  let zonesCache = new Map<string, string>();
  if (technicians.length > 0) {
    const zones = await technicianAPI.getZones();
    zonesCache = new Map(zones.map((zone: any) => [zone.id, zone.name || zone.zone_name]));
  }

  // Enhance technicians with zone names
  const enhancedTechnicians = technicians.map((technician: any) => {
    const assignedZoneIds = Array.isArray(technician.assigned_zone_ids)
      ? technician.assigned_zone_ids
      : technician.assigned_zone_ids
        ? [technician.assigned_zone_ids]
        : [];

    const assignedZoneNames = assignedZoneIds
      .map((zoneId: string) => zonesCache.get(zoneId))
      .filter((name: string | undefined) => name) as string[];

    return {
      ...technician,
      assigned_zone_ids: assignedZoneIds,
      assigned_zone_names: assignedZoneNames
    };
  });

  return {
    technicians: enhancedTechnicians,
    zonesCache
  };
}

/**
 * React Query hook to fetch technicians
 * Uses aggressive caching (10 min staleTime) since technician data changes infrequently
 */
export function useTechniciansQuery() {
  const tenantId = useCurrentTenantId();

  return useQuery({
    queryKey: ['technicians', tenantId],
    queryFn: fetchTechniciansData,
    enabled: !!tenantId,
    staleTime: 10 * 60 * 1000, // 10 minutes - technicians change infrequently
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache longer
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2
  });
}

/**
 * Mutation hook for creating a technician
 */
export function useCreateTechnician() {
  const queryClient = useQueryClient();
  const tenantId = useCurrentTenantId();

  return useMutation({
    mutationFn: async (technicianData: CreateTechnicianData) => {
      const newTechnician = await technicianAPI.createTechnician(technicianData);
      return newTechnician;
    },
    onMutate: async technicianData => {
      await queryClient.cancelQueries({ queryKey: ['technicians', tenantId] });

      const previousData = queryClient.getQueryData<ReturnType<typeof fetchTechniciansData>>([
        'technicians',
        tenantId
      ]);

      // Create optimistic technician with temporary ID
      const optimisticId = 'temp_' + Date.now();
      const optimisticTechnician: Technician = {
        ...technicianData,
        id: optimisticId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        assigned_zone_ids: technicianData.assigned_zone_ids || [],
        assigned_zone_names: []
      } as Technician;

      if (previousData) {
        queryClient.setQueryData(['technicians', tenantId], {
          ...previousData,
          technicians: [optimisticTechnician, ...previousData.technicians]
        });
      }

      return { previousData, optimisticId };
    },
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['technicians', tenantId], context.previousData);
      }
    },
    onSuccess: newTechnician => {
      const metadata = {
        technician_id: newTechnician?.id,
        technician_name: newTechnician?.full_name
      };
      logUserActivity({
        category: 'technician_config',
        action: 'create_technician',
        entityType: 'technician',
        entityId: newTechnician?.id,
        entityName: newTechnician?.full_name,
        metadata: {
          ...metadata,
          human_description: generateHumanDescription(
            'technician_config',
            'create_technician',
            metadata
          )
        }
      });
      // Refetch to get full data with zones
      queryClient.invalidateQueries({ queryKey: ['technicians', tenantId] });
    }
  });
}

/**
 * Mutation hook for updating a technician
 */
export function useUpdateTechnician() {
  const queryClient = useQueryClient();
  const tenantId = useCurrentTenantId();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateTechnicianData }) => {
      const updatedTechnician = await technicianAPI.updateTechnician(id, updates);
      return updatedTechnician;
    },
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey: ['technicians', tenantId] });

      const previousData = queryClient.getQueryData<ReturnType<typeof fetchTechniciansData>>([
        'technicians',
        tenantId
      ]);

      // Optimistically update
      if (previousData) {
        const updatedTechnicians = previousData.technicians.map(tech => {
          if (tech.id === id) {
            return {
              ...tech,
              ...updates,
              // Preserve zone names if zone IDs haven't changed
              assigned_zone_names:
                updates.assigned_zone_ids !== undefined
                  ? [] // Will be recalculated on refetch
                  : tech.assigned_zone_names
            };
          }
          return tech;
        });

        queryClient.setQueryData(['technicians', tenantId], {
          ...previousData,
          technicians: updatedTechnicians
        });
      }

      return { previousData };
    },
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['technicians', tenantId], context.previousData);
      }
    },
    onSuccess: (updatedTechnician, variables) => {
      const metadata = {
        technician_id: variables.id,
        technician_name: updatedTechnician?.full_name || variables.updates.full_name,
        fields_updated: Object.keys(variables.updates)
      };
      logUserActivity({
        category: 'technician_config',
        action: 'update_technician',
        entityType: 'technician',
        entityId: variables.id,
        entityName: updatedTechnician?.full_name,
        metadata: {
          ...metadata,
          human_description: generateHumanDescription(
            'technician_config',
            'update_technician',
            metadata
          )
        }
      });
      // Refetch to get updated zone names
      queryClient.invalidateQueries({ queryKey: ['technicians', tenantId] });
    }
  });
}

/**
 * Mutation hook for deleting a technician
 */
export function useDeleteTechnician() {
  const queryClient = useQueryClient();
  const tenantId = useCurrentTenantId();

  return useMutation({
    mutationFn: async (id: string) => {
      await technicianAPI.deleteTechnician(id);
      return id;
    },
    onMutate: async id => {
      await queryClient.cancelQueries({ queryKey: ['technicians', tenantId] });

      const previousData = queryClient.getQueryData<ReturnType<typeof fetchTechniciansData>>([
        'technicians',
        tenantId
      ]);

      // Capture deleted technician info for audit logging
      const deletedTechnician = previousData?.technicians.find(tech => tech.id === id);

      // Optimistically delete
      if (previousData) {
        queryClient.setQueryData(['technicians', tenantId], {
          ...previousData,
          technicians: previousData.technicians.filter(tech => tech.id !== id)
        });
      }

      return { previousData, deletedTechnician };
    },
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['technicians', tenantId], context.previousData);
      }
    },
    onSuccess: (_data, variables, context) => {
      const metadata = {
        technician_id: variables,
        technician_name: context?.deletedTechnician?.full_name
      };
      logUserActivity({
        category: 'technician_config',
        action: 'delete_technician',
        entityType: 'technician',
        entityId: variables,
        entityName: context?.deletedTechnician?.full_name,
        metadata: {
          ...metadata,
          human_description: generateHumanDescription(
            'technician_config',
            'delete_technician',
            metadata
          )
        }
      });
      queryClient.invalidateQueries({ queryKey: ['technicians', tenantId] });
    }
  });
}

/**
 * Mutation hook for saving technician work schedule
 */
export function useSaveTechnicianWorkSchedule() {
  const queryClient = useQueryClient();
  const tenantId = useCurrentTenantId();

  return useMutation({
    mutationFn: async ({
      technicianId,
      scheduleData
    }: {
      technicianId: string;
      scheduleData: any;
    }) => {
      return await technicianAPI.saveTechnicianWorkSchedule(technicianId, scheduleData);
    },
    onSuccess: (_data, variables) => {
      // Get technician name from cache for audit logging
      const cachedData = queryClient.getQueryData<Awaited<ReturnType<typeof fetchTechniciansData>>>(
        ['technicians', tenantId]
      );
      const technician = cachedData?.technicians.find(t => t.id === variables.technicianId);
      const metadata = {
        technician_id: variables.technicianId,
        technician_name: technician?.full_name
      };
      logUserActivity({
        category: 'technician_config',
        action: 'update_work_schedule',
        entityType: 'technician',
        entityId: variables.technicianId,
        entityName: technician?.full_name,
        metadata: {
          ...metadata,
          human_description: generateHumanDescription(
            'technician_config',
            'update_work_schedule',
            metadata
          )
        }
      });
      // Refetch technicians to get updated schedule data
      queryClient.invalidateQueries({ queryKey: ['technicians', tenantId] });
    }
  });
}
